import { useEffect, useMemo, useRef, useState } from 'react';

import type { DutyLineNote } from 'RestAPIs/employees/hours-table/types';
import type { EmployeeNotesResponse } from 'RestAPIs/employees/notes';

type NoteSliderNote = DutyLineNote | EmployeeNotesResponse;

type UseNoteSliderNavigationProps = {
  notes: NoteSliderNote[];
  isCompact: boolean;
};

const MIN_VISIBLE_INDICATORS = 5;
const DOT_SIZE = 8;
const DOT_GAP = 8;
const INDICATOR_EDGE_PADDING = 8;
const HIDDEN_COUNT_WIDTH = 28;

const getResponsiveIndicatorCount = ({
  containerWidth,
  isCompact
}: {
  containerWidth: number;
  isCompact: boolean;
}) => {
  const maxVisibleIndicators = isCompact ? 9 : 13;
  const reservedSideSpace = isCompact ? 88 : 104;

  const availableWidth = Math.max(
    containerWidth -
      reservedSideSpace -
      HIDDEN_COUNT_WIDTH * 2 -
      INDICATOR_EDGE_PADDING,
    DOT_SIZE
  );

  const rawCount = Math.floor(
    (availableWidth + DOT_GAP) / (DOT_SIZE + DOT_GAP)
  );

  const boundedCount = Math.max(
    MIN_VISIBLE_INDICATORS,
    Math.min(rawCount, maxVisibleIndicators)
  );

  // Keep an odd number so the active indicator can stay visually centered.
  return boundedCount % 2 === 0 ? boundedCount - 1 : boundedCount;
};

const useNoteSliderNavigation = ({
  notes,
  isCompact
}: UseNoteSliderNavigationProps) => {
  const orderedNotes = useMemo(() => [...notes].reverse(), [notes]);
  const [[page, direction], setPage] = useState<[number, number]>([0, 0]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [maxVisibleIndicators, setMaxVisibleIndicators] = useState(
    isCompact ? 9 : 13
  );
  const totalNotes = orderedNotes.length;

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    setMaxVisibleIndicators(
      getResponsiveIndicatorCount({
        containerWidth: element.clientWidth,
        isCompact
      })
    );

    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      setMaxVisibleIndicators(
        getResponsiveIndicatorCount({
          containerWidth: entry.contentRect.width,
          isCompact
        })
      );
    });

    observer.observe(element);

    return () => observer.disconnect();
  }, [isCompact]);

  const safePage = Math.max(0, Math.min(page, totalNotes - 1));

  const visibleIndicatorCount = Math.min(totalNotes, maxVisibleIndicators);
  const halfWindow = Math.floor(visibleIndicatorCount / 2);
  const indicatorWindowStart = Math.max(
    0,
    Math.min(safePage - halfWindow, totalNotes - visibleIndicatorCount)
  );
  const indicatorWindowEnd = indicatorWindowStart + visibleIndicatorCount - 1;
  const hasHiddenIndicatorsOnLeft = indicatorWindowStart > 0;
  const hasHiddenIndicatorsOnRight = indicatorWindowEnd < totalNotes - 1;
  const hiddenIndicatorsOnLeftCount = indicatorWindowStart;
  const hiddenIndicatorsOnRightCount = totalNotes - indicatorWindowEnd - 1;

  const visibleIndicatorIndices = useMemo(
    () =>
      Array.from(
        { length: visibleIndicatorCount },
        (_, offset) => indicatorWindowStart + offset
      ),
    [indicatorWindowStart, visibleIndicatorCount]
  );

  // Keep state in sync if note count changes.
  useEffect(() => {
    if (page !== safePage) setPage([safePage, direction]);
  }, [page, safePage, direction]);

  const prevLen = useRef(orderedNotes.length);
  useEffect(() => {
    if (orderedNotes.length > prevLen.current) {
      setPage([0, -1]);
    }
    prevLen.current = orderedNotes.length;
  }, [orderedNotes.length]);

  const paginate = (dir: number) => {
    if (!totalNotes) return;
    setPage(([currentPage]) => [
      (currentPage + dir + totalNotes) % totalNotes,
      dir
    ]);
  };

  const jumpToPage = (targetPage: number) => {
    if (!totalNotes) return;

    const boundedPage = Math.max(0, Math.min(targetPage, totalNotes - 1));
    const jumpDirection =
      boundedPage > safePage ? 1 : boundedPage < safePage ? -1 : 0;

    setPage([boundedPage, jumpDirection]);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!totalNotes) return;

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      paginate(-1);
      return;
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      paginate(1);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      jumpToPage(0);
      return;
    }

    if (event.key === 'End') {
      event.preventDefault();
      jumpToPage(totalNotes - 1);
    }
  };

  const current = orderedNotes[safePage];
  const currentId = current
    ? 'noteId' in current
      ? current.noteId
      : current.id
    : null;

  return {
    containerRef,
    direction,
    safePage,
    setPage,
    totalNotes,
    hasHiddenIndicatorsOnLeft,
    hasHiddenIndicatorsOnRight,
    hiddenIndicatorsOnLeftCount,
    hiddenIndicatorsOnRightCount,
    visibleIndicatorIndices,
    paginate,
    jumpToPage,
    handleKeyDown,
    current,
    currentId
  };
};

export default useNoteSliderNavigation;
